import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  signal,
  Signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAbstractFilterInput, GalaxyFilterValueInterface } from '@vendasta/galaxy/filter/chips';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SimplifiedUser } from '@galaxy/crm/static';
import { CRMUserService, userFilterFn } from '../../user.service';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, take } from 'rxjs/operators';

@Component({
  template: `
    <glxy-form-field bottomSpacing="'none'">
      <mat-chip-grid #teamMemberGrid aria-label="Team member selection">
        <ng-container *ngIf="loadingInitialValues(); else loadedChips">
          <mat-spinner [diameter]="24"></mat-spinner>
        </ng-container>
        <ng-template #loadedChips>
          <mat-chip-row *ngFor="let teamMember of selectedTeamMembers()" (removed)="remove(teamMember.userId)">
            {{ teamMember.displayName }}
            <button matChipRemove [attr.aria-label]="'remove ' + teamMember.displayName">
              <mat-icon>cancel</mat-icon>
            </button>
          </mat-chip-row>
        </ng-template>
        <input
          matInput
          #teamMemberInput
          [placeholder]="'TABLE_FILTERS.SELECT_EXPRESSION_PLACEHOLDER' | translate"
          [formControl]="formControl"
          [matChipInputFor]="teamMemberGrid"
          [matAutocomplete]="auto"
          [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
          [matChipInputAddOnBlur]="false"
          (matChipInputTokenEnd)="add($event)"
          data-testid="filter-additional-team-member-input"
        />
      </mat-chip-grid>
      <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selected($event)">
        <mat-option *ngFor="let option of filteredOptions()" [value]="option?.userId">
          {{ option?.displayName }}
        </mat-option>
      </mat-autocomplete>
    </glxy-form-field>
  `,
  providers: [
    {
      provide: GalaxyAbstractFilterInput,
      useExisting: forwardRef(() => AdditionalTeamMemberFilterInputComponent),
    },
  ],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
})
export class AdditionalTeamMemberFilterInputComponent implements GalaxyAbstractFilterInput {
  @ViewChild('teamMemberInput') teamMemberInput: ElementRef<HTMLInputElement>;
  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();
  formControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
  separatorKeysCodes: number[] = [ENTER, COMMA];
  loadingInitialValues = signal(false);
  searchTerm = toSignal(this.formControl.valueChanges, { initialValue: '' });
  _fieldId = signal('');
  availableOptions = signal<SimplifiedUser[]>([]);
  filteredOptions: Signal<SimplifiedUser[]> = computed(() => {
    const searchTerm = this.searchTerm();
    const availableOptions = this.availableOptions();
    return availableOptions.filter((tm) => userFilterFn(tm, searchTerm));
  });
  selectedTeamMemberIDs = signal<string[]>([]);
  selectedTeamMembers = signal<SimplifiedUser[]>([]);

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    const salespersonIds = (value ?? []).map((v) => v.string);
    this.selectedTeamMemberIDs.set(salespersonIds);
  }

  constructor(private readonly userService: CRMUserService) {
    effect(() => {
      const selectedTeamMemberIDs = this.selectedTeamMemberIDs();
      this.valueChanges.emit(selectedTeamMemberIDs.map((s) => ({ string: s })));
    });

    this.searchUsers('').subscribe((allTeamMembers) => {
      this.availableOptions.set(allTeamMembers);
      this.loadingInitialValues.set(false);
    });

    this.formControl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        concatMap((searchValue) =>
          this.searchUsers(searchValue).pipe(
            map((results) => {
              return { searchValue, results };
            }),
          ),
        ),
      )
      .subscribe((newValue) => {
        this.availableOptions.set(newValue.results);
        const selectedTeamMember = this.selectedTeamMemberIDs();
        if (selectedTeamMember === undefined || selectedTeamMember.length === 0) {
          return;
        }
        this.userService
          .getMultiUsers(selectedTeamMember)
          .pipe(
            take(1),
            catchError(() => {
              return of([]);
            }),
          )
          .subscribe((sUsr) => {
            this.selectedTeamMembers.set(sUsr);
          });
      });
  }

  searchUsers(searchTerm): Observable<SimplifiedUser[]> {
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.userId;
    return this.userService.searchUsers(filterTerm);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const currentTeamMember = this.selectedTeamMemberIDs();
    const hasTeamMember = currentTeamMember.some((sp) => sp === event.option.value);
    if (event.option.value && !hasTeamMember) {
      this.selectedTeamMemberIDs.set([...currentTeamMember, event.option.value]);
    }
    this.teamMemberInput.nativeElement.value = '';
    this.formControl.setValue('');
  }

  add(event: MatChipInputEvent) {
    const currentTeamMember = this.selectedTeamMemberIDs();
    const hasTeamMember = currentTeamMember.some((sp) => sp === event?.value);
    if (event?.value && !hasTeamMember) {
      this.selectedTeamMemberIDs.set([...currentTeamMember, event.value]);
    }

    // Clear the input value
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    event.chipInput!.clear();
    this.formControl.setValue('');
  }

  remove(teamMemberId: string) {
    const currentTeamMember = this.selectedTeamMemberIDs();
    const listWithoutSelected = currentTeamMember.filter((id) => id !== teamMemberId);
    this.selectedTeamMemberIDs.set([...listWithoutSelected]);
  }

  clearValue() {
    this.formControl.setValue('');
  }
}
